import Papa from "papaparse";
import {getAcwrConfig} from "@/utils/gpsConfig";

// ── Column name normalizer ────────────────────────────────────────────────────
// Strips all non-alphanumeric chars so UTF-8/Latin-1 encoding variance is
// collapsed: "N°" and "NÂ°" both become "n".
const norm = (s) =>
	(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// ── Column pattern → internal key mapping ────────────────────────────────────
// NOTE: Order matters — put more-specific patterns before shorter ones that
// are substrings of them (e.g. "distacc" before "nacc").
const COL_PATTERNS = [
	// Identity
	{pattern: "calciatore",            key: "playerName"},

	// Distances — specific first to avoid substring collisions
	{pattern: "distacc",               key: "distAcc"},
	{pattern: "distdec",               key: "distDec"},
	{pattern: "dist1520",              key: "dist1520"},
	{pattern: "dist2025",              key: "dist2025"},
	{pattern: "dist25",                key: "distOver25"},
	{pattern: "distanzatotale",        key: "distTotal"},
	{pattern: "totaledistanza",        key: "distTotal"},
	{pattern: "disttot",               key: "distTotal"},
	{pattern: "distanzatot",           key: "distTotal"},

	// Counts — after dist* so "distacc" doesn't get claimed by "nacc"
	{pattern: "nacc",                  key: "numAcc"},
	{pattern: "ndec",                  key: "numDec"},
	{pattern: "nsprint",               key: "numSprint"},

	// Speed
	{pattern: "maxspeed",              key: "maxSpeed"},
	{pattern: "velocitamassima",       key: "maxSpeed"},
	{pattern: "velmax",                key: "maxSpeed"},
	{pattern: "avgspeed",              key: "avgSpeed"},
	{pattern: "velocitamedia",         key: "avgSpeed"},
	{pattern: "velmed",                key: "avgSpeed"},

	// Acceleration max (A max - GPS)
	{pattern: "amax",                  key: "accMax"},

	// Load / recovery
	{pattern: "acutetrainingload",     key: "atl"},
	{pattern: "chronictrainingload",   key: "ctl"},
	{pattern: "acwrgps",               key: "acwr"},
	{pattern: "acwr",                  key: "acwr"},
	{pattern: "movementload",          key: "movementLoad"},
	{pattern: "caricomotorio",         key: "movementLoad"},
	{pattern: "trimp",                 key: "trimp"},

	// HR
	{pattern: "averagehrmax",          key: "hrAvg"},
	{pattern: "averagehrmaxgps",       key: "hrAvg"},
	{pattern: "hrmax",                 key: "hrMax"},
	{pattern: "fcmax",                 key: "hrMax"},
	{pattern: "hrmed",                 key: "hrAvg"},
	{pattern: "fcmed",                 key: "hrAvg"},

	// Wellness (embedded in K-Sport export)
	{pattern: "scalaborq",             key: "borg"},
	{pattern: "scalaborg",             key: "borg"},
	{pattern: "borg",                  key: "borg"},
	{pattern: "qualitsonno",           key: "sleepQuality"},
	{pattern: "qualitdel",             key: "sleepQuality"},
	{pattern: "fatica",                key: "fatigue"},
	{pattern: "doms",                  key: "doms"},
	{pattern: "scalavaspre",           key: "vasScore"},
	{pattern: "quickrecovery",         key: "quickRecovery"},

	// Duration
	{pattern: "durata",                key: "durationMin"},
	{pattern: "duration",              key: "durationMin"},
	{pattern: "duratatot",             key: "durationMin"},
];

/**
 * Map a single raw CSV header string → internal key.
 * Returns null if no pattern matches (column will be skipped).
 */
const mapHeader = (rawHeader) => {
	const n = norm(rawHeader);
	for (const {pattern, key} of COL_PATTERNS) {
		if (n.includes(pattern)) return key;
	}
	return null;
};

// ── Filename parser ───────────────────────────────────────────────────────────
// Expected: match_2024-03-15_14_30_TeamA_vs_TeamB.csv
//       or: training_2024-03-15_10_00_CategoryU17.csv
export const parseFilename = (filename) => {
	const base = filename.replace(/\.csv$/i, "");
	const parts = base.split("_");

	const sessionType =
		parts[0]?.toLowerCase() === "match" ? "match" : "training";
	const date  = parts[1] ?? null;   // YYYY-MM-DD
	const hh    = parts[2] ?? "00";
	const mm    = parts[3] ?? "00";
	const label = parts.slice(4).join(" ") || null;

	return {sessionType, date, time: `${hh}:${mm}`, label};
};

// ── Main parser ───────────────────────────────────────────────────────────────
/**
 * Parse a K-Sport CSV file.
 *
 * NOTE: We intentionally do NOT use PapaParse's `transformHeader` option
 * because v5 calls that callback twice per column (once on the raw header,
 * then again on the already-transformed value), which corrupts the mapping.
 * Instead we build the header→key map ourselves from `result.meta.fields`.
 */
export const parseKSportCSV = (text, filename) => {
	const meta = parseFilename(filename);

	const result = Papa.parse(text, {
		header: true,
		skipEmptyLines: true,
	});

	// Build a one-time mapping from raw CSV header → internal key
	const headerMap = {};
	for (const h of result.meta.fields ?? []) {
		const key = mapHeader(h);
		if (key) headerMap[h] = key;
	}

	const rows = result.data
		.map((row) => {
			const mapped = {};
			for (const [h, v] of Object.entries(row)) {
				const key = headerMap[h];
				if (!key) continue; // ignore unmapped columns
				// Convert numeric strings ("7765", "27.2") to numbers
				const num = parseFloat(
					(v ?? "").toString().replace(",", "."),
				);
				mapped[key] = isNaN(num) ? v : num;
			}
			return mapped;
		})
		.filter(
			(row) => row.playerName && row.playerName.trim().length > 0,
		);

	return {meta, rows};
};

// ── ACWR status helper ────────────────────────────────────────────────────────
// Accepts an optional `config` object so callers can pass a cached value
// instead of hitting localStorage on every render. Falls back to getAcwrConfig().
export const acwrStatus = (acwr, config) => {
	if (acwr == null || isNaN(acwr) || acwr <= 0) return null;
	const {acwrLow, acwrHigh, acwrMax} = config ?? getAcwrConfig();
	if (acwr > acwrMax)  return {tone: "critical", label: "Alto rischio"};
	if (acwr > acwrHigh) return {tone: "warning", label: "Attenzione"};
	if (acwr >= acwrLow) return {tone: "good", label: "Ottimale"};
	return                {tone: "neutral", label: "Basso"};
};

// ── Metric display config ─────────────────────────────────────────────────────
export const GPS_METRICS = [
	{key: "distTotal",    label: "Dist. Totale",   unit: "m",    decimals: 0},
	{key: "maxSpeed",     label: "Vel. Max",        unit: "km/h", decimals: 1},
	{key: "numAcc",       label: "Acc.",            unit: "",     decimals: 0},
	{key: "numDec",       label: "Dec.",            unit: "",     decimals: 0},
	{key: "distOver25",   label: ">25 km/h",        unit: "m",    decimals: 0},
	{key: "dist2025",     label: "20–25 km/h",      unit: "m",    decimals: 0},
	{key: "dist1520",     label: "15–20 km/h",      unit: "m",    decimals: 0},
	{key: "distAcc",      label: "Dist Acc",        unit: "m",    decimals: 0},
	{key: "distDec",      label: "Dist Dec",        unit: "m",    decimals: 0},
	{key: "accMax",       label: "Acc Max",         unit: "g",    decimals: 1},
	{key: "atl",          label: "ATL",             unit: "",     decimals: 1},
	{key: "ctl",          label: "CTL",             unit: "",     decimals: 1},
	{key: "acwr",         label: "ACWR",            unit: "",     decimals: 2},
	{key: "movementLoad", label: "Mov. Load",       unit: "",     decimals: 0},
	{key: "trimp",        label: "TRIMP",           unit: "",     decimals: 0},
	{key: "hrAvg",        label: "HR Med %",        unit: "%",    decimals: 0},
];
